import { gql } from "@apollo/client";
import {
  INTERVIEW_PLAN_FRAGMENT,
  INTERVIEW_PLAN_ITEM_FRAGMENT,
} from "client/components/interview-plan/__fragments";
import { JobRoleGuideTemplateForGuideEditorFragment } from "generated/schemaTypes";

import { ATS_STAGE_FOR_EDITOR_FRAGMENT } from "./AtsStage";
import { CONTENT_TEMPLATE_FOR_EDITOR_FRAGMENT } from "./ContentTemplate";
import { EMAIL_TEMPLATE_FOR_EDITOR_FRAGMENT } from "./EmailTemplate";
import { GUIDE_EDITOR_EVENT_TEMPLATES_FRAGMENT } from "./EventTemplate";
import {
  GUIDE_COLLECTION_PROPAGATION_FIELD_CHANGE,
  GUIDE_PROPAGATION_FIELD_CHANGE,
  STAGE_PROPAGATION_FIELD_CHANGE,
} from "./FieldChanges";
import { INTERVIEWER_FOR_EDITOR_FRAGMENT } from "./Interviewer";
import { POST_TEMPLATE_FOR_EDITOR_FRAGMENT } from "./PostTemplate";
import {
  GUIDE_EDITOR_SCHEDULER_CONFIG_AVAILABILITY_FRAGMENT,
  GUIDE_EDITOR_SCHEDULER_CONFIG_EVENT_TEMPLATE_FRAGMENT,
} from "./SchedulerConfig";

export const GUIDE_EDITOR_JOB_ROLE_GUIDE_TEMPLATE_STAGE_SCHEDULER_CONFIG_FRAGMENT = gql`
  fragment GuideEditorJobRoleGuideTemplateStageSchedulerConfigFragment on JobRoleGuideTemplateStageSchedulerConfig {
    id
    eventTemplate {
      ...GuideEditorSchedulerConfigEventTemplateFragment
    }
    schedulerAvailability {
      ...GuideEditorSchedulerConfigAvailabilityFragment
    }
    configInterviewers {
      id
      interviewer {
        ...InterviewerForEditorFragment
      }
    }
  }

  ${GUIDE_EDITOR_SCHEDULER_CONFIG_EVENT_TEMPLATE_FRAGMENT}
  ${GUIDE_EDITOR_SCHEDULER_CONFIG_AVAILABILITY_FRAGMENT}
  ${INTERVIEWER_FOR_EDITOR_FRAGMENT}
`;

export const JOB_ROLE_GUIDE_TEMPLATE_STAGE_FOR_EDITOR_FRAGMENT = gql`
  fragment JobRoleGuideTemplateStageForGuideEditorFragment on JobRoleGuideTemplateStage {
    id
    name
    description
    position
    emailActionCC
    emailActionBCC
    hidden
    contentTemplate {
      ...ContentTemplateForEditorFragment
    }
    emailTemplate {
      ...EmailTemplateForEditorFragment
    }
    postTemplate {
      ...PostTemplateForEditorFragment
    }
    postTemplates {
      ...PostTemplateForEditorFragment
    }
    atssyncJobStage {
      id
      name
      originalATSId
    }
    schedulerConfig {
      ...GuideEditorJobRoleGuideTemplateStageSchedulerConfigFragment
    }
    changes {
      ...StagePropagationFieldChange
    }
  }
  ${EMAIL_TEMPLATE_FOR_EDITOR_FRAGMENT}
  ${CONTENT_TEMPLATE_FOR_EDITOR_FRAGMENT}
  ${POST_TEMPLATE_FOR_EDITOR_FRAGMENT}
  ${GUIDE_EDITOR_JOB_ROLE_GUIDE_TEMPLATE_STAGE_SCHEDULER_CONFIG_FRAGMENT}
  ${STAGE_PROPAGATION_FIELD_CHANGE}
`;

export const JOB_ROLE_GUIDE_TEMPLATE_FOR_EDITOR_FRAGMENT = gql`
  fragment JobRoleGuideTemplateForGuideEditorFragment on JobRoleGuideTemplate {
    id
    name
    jobDisplayName
    chatEnabled
    interviewProcessEnabled
    jobRoleNameOverride
    inProgressGuidesCount
    setupStatus
    defaultGuideVersion
    guideBanner
    guideContent
    changes {
      ...GuidePropagationFieldChange
    }
    collectionChanges {
      ...GuideCollectionPropagationFieldChange
    }
    jobRoleGuideTemplateStages {
      ...JobRoleGuideTemplateStageForGuideEditorFragment
    }
    interviewPlan {
      ...InterviewPlanFragment
      items {
        ...InterviewPlanItemFragment
        postTemplates {
          id
        }
      }
    }
    guideTemplate {
      id
      name
    }
    atsJob {
      id
      atsJobStages {
        ...AtsStageForEditorFragment
      }
    }
    eventTemplates {
      ...GuideEditorEventTemplateFragment
    }
  }
  ${ATS_STAGE_FOR_EDITOR_FRAGMENT}
  ${JOB_ROLE_GUIDE_TEMPLATE_STAGE_FOR_EDITOR_FRAGMENT}
  ${GUIDE_EDITOR_EVENT_TEMPLATES_FRAGMENT}
  ${GUIDE_PROPAGATION_FIELD_CHANGE}
  ${GUIDE_COLLECTION_PROPAGATION_FIELD_CHANGE}
  ${INTERVIEW_PLAN_FRAGMENT}
  ${INTERVIEW_PLAN_ITEM_FRAGMENT}
`;

export const JOB_ROLE_GUIDE_TEMPLATE_VARIABLES_FRAGMENT = gql`
  fragment JobRoleGuideTemplateVariableValuesFragment on JobRoleGuideTemplate {
    jobRoleGuideLexicalContext {
      id
      jobTitle
      jobListingUrl
      companyName
    }
  }
`;

type JobRoleGuideTemplate = JobRoleGuideTemplateForGuideEditorFragment;

export default JobRoleGuideTemplate;
